
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

interface Request {
  id: string;
  type: 'new' | 'replace' | 'pickup';
  status: 'new' | 'inProgress' | 'completed';
  location: string;
  containerNumber?: string;
  date: string;
  notes?: string;
}

export default function RequestsPage() {
  const [requests, setRequests] = useState<Request[]>([]);
  const [filter, setFilter] = useState<'all' | 'new' | 'inProgress' | 'completed'>('all');
  const navigate = useNavigate();

  useEffect(() => {
    // محاكاة البيانات
    const mockRequests: Request[] = [
      {
        id: '1',
        type: 'new',
        status: 'completed',
        location: 'شارع الملك فهد، الرياض',
        date: '2024-01-15',
        notes: 'تم التوصيل بنجاح'
      },
      {
        id: '2',
        type: 'replace',
        status: 'inProgress',
        location: 'حي النخيل، جدة',
        containerNumber: 'C-2024-001',
        date: '2024-01-16',
        notes: 'في الطريق للموقع'
      },
      {
        id: '3',
        type: 'pickup',
        status: 'new',
        location: 'شارع العليا، الرياض',
        containerNumber: 'C-2024-002',
        date: '2024-01-17'
      },
      {
        id: '4',
        type: 'new',
        status: 'completed',
        location: 'حي الصفا، مكة المكرمة',
        date: '2024-01-14',
        notes: 'تم التوصيل في الوقت المحدد'
      },
      {
        id: '5',
        type: 'replace',
        status: 'inProgress',
        location: 'شارع التحلية، الخبر',
        containerNumber: 'C-2024-003',
        date: '2024-01-16'
      }
    ];
    setRequests(mockRequests);
  }, []);

  const getTypeLabel = (type: string) => {
    switch (type) {
      case 'new': return 'طلب حاوية جديدة';
      case 'replace': return 'تبديل حاوية';
      case 'pickup': return 'رفع نهائي';
      default: return type;
    }
  };

  const getStatusLabel = (status: string) => {
    switch (status) {
      case 'new': return 'جديد';
      case 'inProgress': return 'قيد التنفيذ';
      case 'completed': return 'منجز';
      default: return status;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'new': return 'bg-yellow-100 text-yellow-800';
      case 'inProgress': return 'bg-blue-100 text-blue-800';
      case 'completed': return 'bg-green-100 text-green-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getTypeIcon = (type: string) => {
    switch (type) {
      case 'new': return 'ri-add-box-line';
      case 'replace': return 'ri-refresh-line';
      case 'pickup': return 'ri-truck-line';
      default: return 'ri-file-line';
    }
  };

  const getTypeColor = (type: string) => {
    switch (type) {
      case 'new': return 'from-green-500 to-green-600';
      case 'replace': return 'from-blue-500 to-blue-600';
      case 'pickup': return 'from-orange-500 to-orange-600';
      default: return 'from-gray-500 to-gray-600';
    }
  };

  const filteredRequests = requests.filter(request => 
    filter === 'all' || request.status === filter
  );

  return (
    <div className="min-h-screen bg-gray-50" dir="rtl">
      {/* Header */}
      <div className="bg-gradient-to-r from-green-500 to-blue-500 text-white p-4 fixed w-full top-0 z-10">
        <div className="flex items-center">
          <button
            onClick={() => navigate('/')}
            className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center ml-3 hover:bg-white/30 transition-colors"
          >
            <i className="ri-arrow-right-line text-xl"></i>
          </button>
          <div>
            <h1 className="font-bold text-lg">طلباتي</h1>
            <p className="text-sm opacity-90">متابعة حالة الطلبات</p>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="pt-20 pb-20 px-4">
        {/* Filter Tabs */}
        <div className="bg-white rounded-2xl shadow-sm p-4 mb-6">
          <div className="grid grid-cols-4 gap-2">
            {[
              { key: 'all', label: 'الكل', count: requests.length },
              { key: 'new', label: 'جديد', count: requests.filter(r => r.status === 'new').length },
              { key: 'inProgress', label: 'قيد التنفيذ', count: requests.filter(r => r.status === 'inProgress').length },
              { key: 'completed', label: 'منجز', count: requests.filter(r => r.status === 'completed').length }
            ].map((tab) => (
              <button
                key={tab.key}
                onClick={() => setFilter(tab.key as any)}
                className={`py-3 px-2 rounded-lg text-sm font-medium transition-colors ${
                  filter === tab.key
                    ? 'bg-green-500 text-white'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              >
                <div>{tab.label}</div>
                <div className="text-xs opacity-75">({tab.count})</div>
              </button>
            ))}
          </div>
        </div>

        {/* Requests List */}
        <div className="space-y-4">
          {filteredRequests.length === 0 ? (
            <div className="bg-white rounded-2xl shadow-sm p-8 text-center">
              <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-file-list-line text-gray-400 text-2xl"></i>
              </div>
              <h3 className="font-bold text-gray-800 mb-2">لا توجد طلبات</h3>
              <p className="text-gray-600 text-sm">لم يتم العثور على طلبات بالفلتر المحدد</p>
            </div>
          ) : (
            filteredRequests.map((request) => (
              <div key={request.id} className="bg-white rounded-2xl shadow-sm p-6">
                <div className="flex items-start">
                  <div className={`w-12 h-12 bg-gradient-to-r ${getTypeColor(request.type)} rounded-xl flex items-center justify-center ml-4 flex-shrink-0`}>
                    <i className={`${getTypeIcon(request.type)} text-white text-xl`}></i>
                  </div>
                  
                  <div className="flex-1 text-right">
                    <div className="flex items-center justify-between mb-2">
                      <span className={`px-3 py-1 rounded-full text-xs font-medium ${getStatusColor(request.status)}`}>
                        {getStatusLabel(request.status)}
                      </span>
                      <h3 className="font-bold text-gray-800">{getTypeLabel(request.type)}</h3>
                    </div>
                    
                    <div className="space-y-2 text-sm text-gray-600">
                      <div className="flex items-center justify-end">
                        <span className="mr-2">{request.location}</span>
                        <i className="ri-map-pin-line text-gray-400"></i>
                      </div>
                      
                      {request.containerNumber && (
                        <div className="flex items-center justify-end">
                          <span className="mr-2">رقم الحاوية: {request.containerNumber}</span>
                          <i className="ri-barcode-line text-gray-400"></i>
                        </div>
                      )}
                      
                      <div className="flex items-center justify-end">
                        <span className="mr-2">{new Date(request.date).toLocaleDateString('ar-SA')}</span>
                        <i className="ri-calendar-line text-gray-400"></i>
                      </div>
                      
                      {request.notes && (
                        <div className="flex items-start justify-end">
                          <span className="mr-2 text-right">{request.notes}</span>
                          <i className="ri-file-text-line text-gray-400 mt-0.5"></i>
                        </div>
                      )}
                    </div>
                    
                    {/* Progress Indicator */}
                    <div className="mt-4">
                      <div className="flex items-center justify-between text-xs text-gray-500 mb-2">
                        <span>منجز</span>
                        <span>قيد التنفيذ</span>
                        <span>جديد</span>
                      </div>
                      <div className="w-full bg-gray-200 rounded-full h-2">
                        <div 
                          className={`h-2 rounded-full transition-all duration-300 ${
                            request.status === 'completed' ? 'bg-green-500 w-full' :
                            request.status === 'inProgress' ? 'bg-blue-500 w-2/3' :
                            'bg-yellow-500 w-1/3'
                          }`}
                        ></div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>

        {/* Quick Actions */}
        <div className="bg-white rounded-2xl shadow-sm p-6 mt-6">
          <h3 className="font-bold text-gray-800 mb-4 text-right">إجراءات سريعة</h3>
          <div className="grid grid-cols-3 gap-3">
            <button
              onClick={() => navigate('/new-request')}
              className="flex flex-col items-center p-4 bg-green-50 rounded-xl hover:bg-green-100 transition-colors"
            >
              <div className="w-10 h-10 bg-green-500 rounded-full flex items-center justify-center mb-2">
                <i className="ri-add-line text-white"></i>
              </div>
              <span className="text-xs text-green-700 font-medium text-center">طلب جديد</span>
            </button>
            
            <button
              onClick={() => navigate('/replace-request')}
              className="flex flex-col items-center p-4 bg-blue-50 rounded-xl hover:bg-blue-100 transition-colors"
            >
              <div className="w-10 h-10 bg-blue-500 rounded-full flex items-center justify-center mb-2">
                <i className="ri-refresh-line text-white"></i>
              </div>
              <span className="text-xs text-blue-700 font-medium text-center">تبديل</span>
            </button>
            
            <button
              onClick={() => navigate('/pickup-request')}
              className="flex flex-col items-center p-4 bg-orange-50 rounded-xl hover:bg-orange-100 transition-colors"
            >
              <div className="w-10 h-10 bg-orange-500 rounded-full flex items-center justify-center mb-2">
                <i className="ri-truck-line text-white"></i>
              </div>
              <span className="text-xs text-orange-700 font-medium text-center">رفع نهائي</span>
            </button>
          </div>
        </div>
      </div>

      {/* Bottom Navigation */}
      <div className="fixed bottom-0 w-full bg-white border-t border-gray-200 px-4 py-2">
        <div className="grid grid-cols-4 gap-1">
          <button 
            onClick={() => navigate('/')}
            className="flex flex-col items-center py-2 px-1 text-gray-400 hover:text-gray-600"
          >
            <div className="w-6 h-6 flex items-center justify-center mb-1">
              <i className="ri-home-line text-lg"></i>
            </div>
            <span className="text-xs">الرئيسية</span>
          </button>
          <button className="flex flex-col items-center py-2 px-1 text-green-500">
            <div className="w-6 h-6 flex items-center justify-center mb-1">
              <i className="ri-file-list-fill text-lg"></i>
            </div>
            <span className="text-xs">طلباتي</span>
          </button>
          <button 
            onClick={() => navigate('/notifications')}
            className="flex flex-col items-center py-2 px-1 text-gray-400 hover:text-gray-600"
          >
            <div className="w-6 h-6 flex items-center justify-center mb-1">
              <i className="ri-notification-line text-lg"></i>
            </div>
            <span className="text-xs">التنبيهات</span>
          </button>
          <button 
            onClick={() => navigate('/profile')}
            className="flex flex-col items-center py-2 px-1 text-gray-400 hover:text-gray-600"
          >
            <div className="w-6 h-6 flex items-center justify-center mb-1">
              <i className="ri-user-line text-lg"></i>
            </div>
            <span className="text-xs">الملف الشخصي</span>
          </button>
        </div>
      </div>
    </div>
  );
}
